/**
 * The rule, stated as a sentence the trainer edits in place — the mock's `sentence3`.
 *
 * ── The words come from `model/ruleSentence.ts`, not from here ──────────────────────────────
 * It returns TOKENS rather than a string precisely so that two of them can be controls: a `value`
 * token carrying `edit: 'delta'` becomes the stepper, one carrying `edit: 'gateCeiling'` becomes the
 * range field, and every `text` token is a joining word. A string would have to be parsed back to
 * find them. The family chip and the gate-column chip are the two controls the sentence has no token
 * for, because they change WHICH sentence is being read rather than a number inside it; they take
 * the mock's own positions, first and immediately before the ceiling.
 *
 * ── Three controls that write, and one that does not ────────────────────────────────────────
 * The family menu, the gate menu, the stepper and the range field all dispatch into the staging
 * reducer. The family PILL at the right end is a reading of the same state and is help-gated, like
 * every other hint on this surface.
 *
 * ── Every refusal is STATED, never hidden ───────────────────────────────────────────────────
 * A family the exercise has no room for (`fromResultRefusal`), a column another rule already gates
 * (`columnRuleRefusal`), a gate column that cannot decide progression (`gateRefusal`): each renders
 * as a disabled entry that keeps its reason on its own accessible name, and a disabled stepper end
 * stays focusable so the reason is reachable from the keyboard.
 */

import { useRef, useState, type RefObject } from 'react';

import { ConfirmDialog } from '~/shared/ui/ConfirmDialog';

import {
  bandPrefillText,
  carriesProgression,
  columnRuleRefusal,
  columnStepParams,
  defaultGatedField,
  deltaRefusal,
  deltaUnitOptions,
  fromResultRefusal,
  isPercentMode,
  parseNumberOrRange,
  ruleDisplacedByBand,
  ruleFor,
  type ExerciseDraft,
  type ProgressionDeltaUnit,
  type ProgressionRule,
} from '~/entities/plan-draft';
import { measurementFieldDefinition, type MeasurementFieldKey } from '~/shared/measurement';
import menuStyles from '~/shared/ui/MenuSurface.module.css';
import stepper from '~/shared/ui/stepper.module.css';

import { formatNumber, type Translate } from '../model/matrixCellState';
import type {
  ProgressionStagingAction,
  SentenceFamily,
  StagedBand,
} from '../model/progressionStaging';
import {
  columnNounKey,
  formatRuleDelta,
  ruleSentence,
  type SentenceToken,
} from '../model/ruleSentence';

import { useRovingRadioGroup } from '../lib/useRovingRadioGroup';

import { GateMenu } from './GateMenu';
import { RefusableRadioMenu } from './RefusableRadioMenu';
import styles from './RuleSentence.module.css';
import sheetStyles from './ProgressionSheet.module.css';

const KEY = 'web:planBuilder.progression.sheet';

/**
 * What a column READS as: the two rule families, or „Manualna" for a column carrying no rule.
 *
 * These three are the mock's, and they are STATES — each is a true description of a column somebody
 * could reopen the sheet on.
 */
type SentenceState = SentenceFamily | 'none';

/**
 * What the „TYP PROGRESJI" menu OFFERS as a choice: the three states. The reset is an ACT under
 * them, not a fourth entry.
 *
 * „Resetuj progresję" (owner, 15 September 2026, replacing the radio-shaped „Brak progresji") ends
 * where „Manualna" does, with no rule, and takes every hand-set cycle on the column with it in ONE
 * staged act, so the grid reads the base flat. „Manualna" keeps those cycles, because driving them by
 * hand is the point of it. The reset asks first, because what it removes is the trainer's own work.
 */
const FAMILIES: readonly SentenceState[] = ['static', 'fromResult', 'none'];

/**
 * The reading as a COLOUR mark, or nothing.
 *
 * The dot and the pill are coloured per family, and „Manualna" has no colour of its own — the accent
 * is a scalpel, and „this column does nothing" is the last thing on the sheet that should be wearing
 * one. `undefined` leaves the attribute off, which is what the stylesheet's neutral default is keyed
 * on.
 */
function familyMark(family: SentenceState): SentenceFamily | undefined {
  return family === 'none' ? undefined : family;
}

interface RuleSentenceProps {
  readonly staged: ExerciseDraft;
  readonly field: MeasurementFieldKey;
  readonly dispatch: (action: ProgressionStagingAction) => void;
  readonly onBandEdit: (edit: StagedBand | null) => void;
  readonly showHints: boolean;
  readonly t: Translate;
  readonly locale: string;
}

export function RuleSentence({
  staged,
  field,
  dispatch,
  onBandEdit,
  showHints,
  t,
  locale,
}: RuleSentenceProps) {
  const rule = ruleFor(staged.progressionRules, field);
  const sentence = ruleSentence(staged, field, rule);
  const [isConfirmingReset, setIsConfirmingReset] = useState(false);
  /* Where Escape or „Anuluj" lands: the menu item that raised the dialog is unmounted by then, and
   * Base UI's own return target would be `<body>`. */
  const familyTriggerRef = useRef<HTMLButtonElement>(null);
  const family: SentenceState = sentence?.family ?? 'none';
  const isBanded = rule !== null && rule.type === 'PreviousExecution' && rule.banded !== null;
  const gatedField = rule !== null && rule.type === 'PreviousExecution' ? rule.banded : null;

  const context = {
    measurementFields: staged.measurementFields,
    progressionRules: staged.progressionRules,
    prescriptionMode: staged.prescriptionMode,
  };
  const ruleRefusal = columnRuleRefusal(context, field);
  const resultRefusal = fromResultRefusal(staged, field);
  /**
   * R4's refusal, worded once for the two controls that can reach the shape: „Statyczna", which seeds
   * a step, and the stepper itself, which a plan authored before this guard can still arrive holding.
   */
  const stepReason = stepRefusalText(staged, field, ruleFor(staged.progressionRules, field), t, locale);
  /** Null when no column in this movement could carry a band — then the switch has nothing to turn on. */
  const gateCandidate = defaultGatedField(context, field);
  /** The column whose own rule turning the switch ON would destroy. The SAME predicate the writer uses. */
  const displaced =
    isBanded || gateCandidate === null
      ? null
      : ruleDisplacedByBand(staged.progressionRules, field, gateCandidate);

  return (
    <div className={styles.box}>
      <div className={styles.row}>
        <span className={styles.field}>
          <span className={styles.fieldLabel}>{t(`${KEY}.field.family`)}</span>
          <FamilyMenu
            family={family}
            ruleRefusal={ruleRefusal}
            resultRefusal={resultRefusal}
            stepReason={stepReason}
            triggerRef={familyTriggerRef}
            onPick={(next) => {
              /* „Manualna" takes the rule off and leaves every pinned cycle where the trainer put it.
               * The reset below is the other way to end with no rule. */
              if (next === 'none') {
                dispatch({ type: 'clearColumnRule', field });
                onBandEdit(null);
                return;
              }
              dispatch({ type: 'setColumnFamily', field, family: next });
            }}
            onReset={() => {
              setIsConfirmingReset(true);
            }}
            t={t}
          />
          <ConfirmDialog
            prompt={
              isConfirmingReset
                ? {
                    title: t(`${KEY}.reset.title`),
                    subject: staged.name,
                    description: t(`${KEY}.reset.body`, { column: t(columnNounKey(field, false)) }),
                    confirmLabel: t(`${KEY}.reset.confirm`),
                    cancelLabel: t(`${KEY}.reset.cancel`),
                    destructiveConfirm: true,
                  }
                : null
            }
            finalFocus={familyTriggerRef}
            onConfirm={() => {
              /* The rule and the pins go in the SAME staged act, so they share one undo entry when
               * „Zastosuj do draftu" lands. */
              dispatch({ type: 'clearColumnProgression', field });
              onBandEdit(null);
              setIsConfirmingReset(false);
            }}
            onCancel={() => {
              setIsConfirmingReset(false);
            }}
          />
        </span>

        {/* The mock's own order, and the reason `ruleSentence` answers with a LIST: the joining word,
          * the range, the column the client climbs, the second joining word, the step. Which of them
          * appear is the rule's state, which is that module's decision rather than this one's. */}
        {(sentence?.tokens ?? []).map((token, index) => (
          <SentenceSlot
            /* Positional: the token list is regenerated whole on every keystroke and has no id of
             * its own. Nothing here holds state across a render, so a positional key costs nothing. */
            key={`${token.kind}-${String(index)}`}
            token={token}
            staged={staged}
            field={field}
            gatedField={gatedField}
            dispatch={dispatch}
            onBandEdit={onBandEdit}
            t={t}
            locale={locale}
          />
        ))}

        {/* The mock draws the two hints as words at the end of the row, under the `?`. */}
        {showHints && family === 'static' && (
          <span className={`${String(styles.word)} ${String(styles.wordMuted)}`}>
            {t(`${KEY}.hint.static`)}
          </span>
        )}
        {showHints && family === 'none' && (
          <span className={`${String(styles.word)} ${String(styles.wordMuted)}`}>
            {t(`${KEY}.hint.none`)}
          </span>
        )}
      </div>

      <div className={styles.switchRow}>
        {/* THE SWITCH BELONGS TO ONE FAMILY, and under the other three it is not drawn at all.
          *
          * A band is the answer to "when does the client earn the step", and only „Od wyniku" asks
          * that question: a static rule climbs on the calendar whatever the client did, and a column
          * with no rule has no step to gate. Until 15 September 2026 the switch stood under every
          * family, disabled, carrying „przyrost dopiero po dowiezionym cyklu" as its reason — a
          * control that is present, refused and unreachable on three readings out of four, which
          * reads as a broken switch rather than as a rule about families.
          *
          * The mock draws it under „Manualna"; the owner overrode the mock on 15 September 2026 and
          * that departure is recorded in `docs/design/README.md` and this slice's `AGENTS.md`. The
          * MODEL's refusals are untouched — `stagedReducer` still refuses a band the document cannot
          * hold, so nothing moved from a decision into a component. This is visibility only. */}
        {family === 'fromResult' && (
          <>
            <BindSwitch
              isOn={isBanded}
              /* The one refusal left: a movement with no column able to decide progression has
               * nothing to bind to. It stays focusable, so the reason is reachable by keyboard. */
              refusal={gateCandidate === null ? t(`${KEY}.gate.refusal.notDeciding`) : null}
              label={t(`${KEY}.bind.label`)}
              /* The column the switch would bind to may carry a rule of its own, and binding TAKES
               * THAT RULE OFF — `ruleOnColumnPatch` does it in the same patch, because the backend
               * refuses a column that is both banded and ruled. The gate MENU has warned about that
               * since it was built; the switch beside it did it silently. One predicate, two
               * controls, one warning. */
              warning={
                displaced === null
                  ? null
                  : `${t(`${KEY}.gate.replacesRule`)} ${t(`web:planBuilder.day.field.${displaced}`)}`
              }
              onToggle={() => {
                if (isBanded) {
                  dispatch({ type: 'unbindColumn', field });
                  onBandEdit(null);
                  return;
                }
                if (gateCandidate === null) return;
                bindTo(staged, field, gateCandidate, dispatch, onBandEdit);
              }}
            />
            {showHints && isBanded && (
              <span className={sheetStyles.hint}>{t(`${KEY}.bind.hint`)}</span>
            )}
            {/* Drawn whether or not the `?` is on: it is not a hint, it is the price of the next
              * press. The gate MENU states the same thing on the entry that would cost it. */}
            {displaced !== null && (
              <span className={styles.warning}>
                {t(`${KEY}.gate.replacesRule`)} {t(`web:planBuilder.day.field.${displaced}`)}
              </span>
            )}
          </>
        )}
        <span className={styles.spring} />
        {showHints && (
          <span className={styles.familyPill} data-family={familyMark(family)}>
            <span
              className={styles.chipDot}
              data-family={familyMark(family)}
              aria-hidden="true"
            />
            {t(`${KEY}.family.${family}.name`)} · {t(`${KEY}.family.${family}.hint`)}
          </span>
        )}
      </div>

      {showHints && rule !== null && carriesProgression(rule) && (
        <span className={sheetStyles.hint}>
          <strong>{t(`${KEY}.rounding.title`)}</strong> {t(`${KEY}.rounding.body`)}
        </span>
      )}

    </div>
  );
}

/** One token: a joining word, the stepper, or the range field. */
function SentenceSlot({
  token,
  staged,
  field,
  gatedField,
  dispatch,
  onBandEdit,
  t,
  locale,
}: {
  readonly token: SentenceToken;
  readonly staged: ExerciseDraft;
  readonly field: MeasurementFieldKey;
  readonly gatedField: MeasurementFieldKey | null;
  readonly dispatch: (action: ProgressionStagingAction) => void;
  readonly onBandEdit: (edit: StagedBand | null) => void;
  readonly t: Translate;
  readonly locale: string;
}) {
  if (token.kind === 'text') {
    // A parameter's VALUE is a key too — see `ruleSentence`'s header. Resolved here because `t`
    // lives on this side of the seam and nowhere else.
    const params = Object.fromEntries(
      Object.entries(token.paramKeys ?? {}).map(([name, key]) => [name, t(key)]),
    );
    return <span className={styles.word}>{t(token.key, params)}</span>;
  }
  if (token.control === 'delta') {
    return <StepStepper staged={staged} field={field} dispatch={dispatch} t={t} locale={locale} />;
  }
  if (gatedField === null) return null;
  if (token.control === 'range') {
    return (
      <RangeField
        /* KEYED BY THE COLUMN IT IS ABOUT. The field seeds its text once, at mount, from the band
          * standing on `gatedField` — and the slot's own position key is the same whichever column
          * the gate names, so moving the gate from reps to duration left „8–12" in the box and the
          * next blur wrote 8 and 12 onto the 30-second rows. Remounting re-reads the seed; the blur
          * commit is untouched, because a half-typed band must still never travel. */
        key={gatedField}
        staged={staged}
        steppedField={field}
        gatedField={gatedField}
        dispatch={dispatch}
        onBandEdit={onBandEdit}
        t={t}
      />
    );
  }
  return (
    <span className={styles.field}>
      <span className={styles.fieldLabel}>{t(`${KEY}.field.gate`)}</span>
      <GateMenu
        staged={staged}
        steppedField={field}
        gatedField={gatedField}
        onPick={(next) => {
          bindTo(staged, field, next, dispatch, onBandEdit);
        }}
        t={t}
      />
    </span>
  );
}

/**
 * Bind the rule to a column: seed the range from whatever the rows already say, write the band, and
 * remember it for the commit.
 *
 * ONE function, because the gate menu and the switch beside it do the same thing and drifted: the
 * switch used to pass a band the menu did not, which is how the two ends of one act came to write
 * different rows.
 */
function bindTo(
  staged: ExerciseDraft,
  steppedField: MeasurementFieldKey,
  gatedField: MeasurementFieldKey,
  dispatch: (action: ProgressionStagingAction) => void,
  onBandEdit: (edit: StagedBand | null) => void,
): void {
  const reading = parseNumberOrRange(bandPrefillText(staged, gatedField), gatedField);
  const floor = reading.kind === 'range' ? reading.floor : null;
  const ceiling = reading.kind === 'range' ? reading.ceiling : null;
  dispatch({
    type: 'writeBand',
    steppedField,
    gatedField,
    floor,
    ceiling,
    // The gate is only MOVING here; the pair was not typed, so a row carrying its own floor keeps
    // it. See `writeBand`'s note on the wave of 10 / 8 / 6.
    overwriteFloor: false,
  });
  onBandEdit({ gatedField, floor, ceiling, overwriteFloor: false });
}

/**
 * „Przyrost" — the per-cycle step, plus the kg / % segment on the load column.
 *
 * The two ends carry the SHARED accessible names mobile's suite drives (`plans.progression.*`), so
 * one locator finds the same control in both apps. The step's own name is the shared
 * `steppedColumn.stepA11y`, and the `%` pill's is `steppedColumn.unitPercentA11y`.
 */
function StepStepper({
  staged,
  field,
  dispatch,
  t,
  locale,
}: {
  readonly staged: ExerciseDraft;
  readonly field: MeasurementFieldKey;
  readonly dispatch: (action: ProgressionStagingAction) => void;
  readonly t: Translate;
  readonly locale: string;
}) {
  const rule = ruleFor(staged.progressionRules, field);
  if (rule === null) return null;
  const unit: ProgressionDeltaUnit = rule.deltaUnit;
  const params = columnStepParams(field, unit);
  if (params === null) return null;

  const current = rule.delta ?? 0;
  const units = deltaUnitOptions(field);
  const next = (direction: 1 | -1) =>
    Math.round((current + direction * params.step) * 1e6) / 1e6;
  /**
   * R4: an exercise pricing its unpriced rows above the client's whole max may not also ask that
   * price to advance. Stated on the stepper rather than hidden, and the reason rides the accessible
   * name for the reason every other refusal here does — a control that drops out of the tab order
   * takes its own explanation with it.
   */
  const stepReason = stepRefusalText(staged, field, rule, t, locale);
  const atCeiling = stepReason !== null || next(1) > params.max;
  const atFloor = stepReason !== null || next(-1) < params.min;

  const text = formatRuleDelta(field, rule, current, isPercentMode(staged.prescriptionMode), locale);

  return (
    <>
      <span className={styles.field}>
        <span className={styles.fieldLabel}>{t(`${KEY}.field.step`)}</span>
        {/* The kg / % segment stands on the PRZYROST line right after the stepper, with no label of
          * its own: the owner's loop-1 call (C6), carried out on 15 September 2026. As its own
          * labelled field it was stretched to the width of that label. */}
        <span className={styles.stepLine}>
          <span className={stepper.stepper}>
            <button
              type="button"
              className={stepper.button}
              /* Mobile's own name, so one locator drives both suites. */
              aria-label={
                stepReason === null
                  ? t('plans.progression.decrementA11y')
                  : `${t('plans.progression.decrementA11y')}, ${stepReason}`
              }
              /* `aria-disabled` rather than `disabled`: a control that states a reason has to stay in
               * the tab order, or the reason it exists is unreachable. */
              aria-disabled={atFloor}
              onClick={() => {
                if (!atFloor) dispatch({ type: 'setDelta', field, delta: next(-1) });
              }}
            >
              <span aria-hidden="true">−</span>
            </button>
            <output className={stepper.value} aria-label={t('plans.progression.steppedColumn.stepA11y')}>
              {text}
            </output>
            <button
              type="button"
              className={stepper.button}
              aria-label={
                stepReason === null
                  ? t('plans.progression.incrementA11y')
                  : `${t('plans.progression.incrementA11y')}, ${stepReason}`
              }
              aria-disabled={atCeiling}
              onClick={() => {
                if (!atCeiling) dispatch({ type: 'setDelta', field, delta: next(1) });
              }}
            >
              <span aria-hidden="true">+</span>
            </button>
          </span>
          {units.length > 1 && (
            <UnitSegment
              units={units}
              unit={unit}
              field={field}
              onSelect={(option) => {
                if (option !== unit) dispatch({ type: 'setDeltaUnit', field, unit: option });
              }}
              t={t}
            />
          )}
        </span>
      </span>

      {/* The refusal is READ as well as announced. The family menu prints its reasons inside the
        * popup a trainer opens; a stepper has no popup, so the note stands beside it, in the row's
        * own muted word treatment. One string serves both — the name and the note say the same
        * thing, because two wordings of one refusal are two chances to word it differently. */}
      {stepReason !== null && (
        <span className={`${String(styles.word)} ${String(styles.wordMuted)}`}>{stepReason}</span>
      )}
    </>
  );
}

/**
 * The kg / % segment — a single choice, so ONE tab stop and arrows that move it.
 *
 * It claimed `role="radiogroup"` while every option was an ordinary tab stop and no arrow key did
 * anything, which is a promise to a screen reader the control did not keep: „radio, 1 z 2" and then
 * silence on the key the word names. `useRovingRadioGroup` is the pattern, and it was written for
 * exactly these and then left unused.
 */
function UnitSegment({
  units,
  unit,
  field,
  onSelect,
  t,
}: {
  readonly units: readonly ProgressionDeltaUnit[];
  readonly unit: ProgressionDeltaUnit;
  readonly field: MeasurementFieldKey;
  readonly onSelect: (unit: ProgressionDeltaUnit) => void;
  readonly t: Translate;
}) {
  const groupRef = useRef<HTMLSpanElement>(null);
  const group = useRovingRadioGroup({ groupRef, options: units, value: unit, onSelect });

  return (
    <span
      ref={groupRef}
      className={styles.unitSegment}
      aria-label={t(`${KEY}.unit.label`)}
      {...group.groupProps}
    >
      {units.map((option) => (
        <button
          key={option}
          type="button"
          className={styles.unitOption}
          aria-label={
            option === 'Percent'
              ? t('plans.progression.steppedColumn.unitPercentA11y')
              : t(`${KEY}.unit.absoluteA11y`)
          }
          {...group.optionProps(option)}
        >
          {option === 'Percent' ? '%' : (measurementFieldDefinition(field).unit ?? '')}
        </button>
      ))}
    </span>
  );
}

/**
 * „Zakres" — the one control that holds a floor and a ceiling, „8" or „8–12".
 *
 * It commits on blur and on Enter, never per keystroke: a half-typed "8–1" is a legal reading of a
 * different band, and writing it across the rows would leave the trainer's own ceiling behind.
 */
function RangeField({
  staged,
  steppedField,
  gatedField,
  dispatch,
  onBandEdit,
  t,
}: {
  readonly staged: ExerciseDraft;
  readonly steppedField: MeasurementFieldKey;
  readonly gatedField: MeasurementFieldKey;
  readonly dispatch: (action: ProgressionStagingAction) => void;
  readonly onBandEdit: (edit: StagedBand | null) => void;
  readonly t: Translate;
}) {
  const [text, setText] = useState(() => bandPrefillText(staged, gatedField));
  const reading = parseNumberOrRange(text, gatedField);
  const isInvalid = reading.kind === 'invalid';

  const commit = () => {
    if (reading.kind === 'invalid') return;
    if (reading.kind === 'empty') {
      // An ERASED field drops the ceilings and leaves the band's column standing. It is deliberately
      // not the same act as turning the switch off — see `clearBand` in the staging reducer.
      dispatch({ type: 'clearBand' });
      onBandEdit({ gatedField, floor: null, ceiling: null, overwriteFloor: false });
      return;
    }
    const floor = reading.kind === 'range' ? reading.floor : reading.value;
    const ceiling = reading.kind === 'range' ? reading.ceiling : null;
    dispatch({
      type: 'writeBand',
      steppedField,
      gatedField,
      floor,
      ceiling,
      // TYPED as a pair, which is the one case where the floor is the sheet's to write across.
      overwriteFloor: true,
    });
    onBandEdit({ gatedField, floor, ceiling, overwriteFloor: true });
  };

  return (
    <span className={styles.field}>
      <span className={styles.fieldLabel}>{t(`${KEY}.field.range`)}</span>
      <input
        className={styles.range}
        type="text"
        inputMode="numeric"
        value={text}
        placeholder={t(`${KEY}.range.placeholder`)}
        aria-label={t(`${KEY}.range.a11y`)}
        aria-invalid={isInvalid}
        onChange={(event) => {
          setText(event.target.value);
        }}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          commit();
        }}
      />
      {isInvalid && <span className={styles.rangeRefusal}>{t(`${KEY}.range.invalid`)}</span>}
    </span>
  );
}

/**
 * R4's refusal in words, or null where the column may carry a step.
 *
 * One helper because two controls ask it and a refusal worded twice is a refusal worded two ways.
 * A null `rule` asks it of the Static rule „Statyczna" would write.
 */
function stepRefusalText(
  staged: ExerciseDraft,
  field: MeasurementFieldKey,
  rule: ProgressionRule | null,
  t: Translate,
  locale: string,
): string | null {
  const refusal = deltaRefusal(staged, field, rule);
  return refusal === null
    ? null
    : t(`${KEY}.stepRefusal.${refusal}`, {
        multiplier: formatNumber(staged.realMaxMultiplier ?? 0, locale),
      });
}

interface FamilyMenuProps {
  readonly family: SentenceState;
  readonly ruleRefusal: ReturnType<typeof columnRuleRefusal>;
  readonly resultRefusal: ReturnType<typeof fromResultRefusal>;
  /**
   * R4's refusal, already worded, or null. It lands on „Statyczna" rather than on the stepper,
   * because on this sheet picking that family IS picking a step: it seeds the column's opening value,
   * and a flat static rule carries no progression, so it would draw no stepper to refuse.
   */
  readonly stepReason: string | null;
  /** The chip, so a dialog raised from this menu can hand focus back to it. */
  readonly triggerRef: RefObject<HTMLButtonElement | null>;
  readonly onPick: (family: SentenceState) => void;
  /** „Resetuj progresję" was pressed. The caller asks before anything is staged. */
  readonly onReset: () => void;
  readonly t: Translate;
}

/** „Typ progresji" — the three readings, each carrying its own refusal where it has one. */
function FamilyMenu({
  family,
  ruleRefusal,
  resultRefusal,
  stepReason,
  triggerRef,
  onPick,
  onReset,
  t,
}: FamilyMenuProps) {
  const refusalFor = (candidate: SentenceState): string | null => {
    // „Manualna" cannot be refused: taking a rule off is always available.
    if (candidate === 'none') return null;
    if (ruleRefusal !== null) return t(`${KEY}.columnRefusal.${ruleRefusal}`);
    if (candidate === 'static' && stepReason !== null) return stepReason;
    if (candidate === 'fromResult' && resultRefusal !== null) {
      return t(`${KEY}.familyRefusal.${resultRefusal}`);
    }
    return null;
  };

  return (
    <RefusableRadioMenu
      triggerProps={{
        ref: triggerRef,
        className: styles.chip,
        'data-family': familyMark(family) ?? '',
        /* What it is AND what it currently says. An `aria-label` REPLACES the chip's own text, so
          * „Typ progresji" alone left the one word a sighted trainer reads off this control — the
          * family it is set to — audible nowhere. The column chips beside it already pair the two
          * this way. */
        'aria-label': t(`${KEY}.menuValue`, {
          label: t(`${KEY}.family.menuLabel`),
          value: t(`${KEY}.family.${family}.name`),
        }),
        children: (
          <>
            <span
              className={styles.chipDot}
              data-family={familyMark(family)}
              aria-hidden="true"
            />
            {t(`${KEY}.family.${family}.name`)}
            <i className={styles.caret} aria-hidden="true">
              ▾
            </i>
          </>
        ),
      }}
      value={family}
      options={FAMILIES.map((candidate) => {
        const reason = refusalFor(candidate);
        return {
          value: candidate,
          isRefused: reason !== null,
          content: (
            <span className={styles.menuEntry}>
              <span className={menuStyles.itemLabel}>{t(`${KEY}.family.${candidate}.name`)}</span>
              <span className={styles.menuNote}>
                {reason ?? t(`${KEY}.family.${candidate}.desc`)}
              </span>
            </span>
          ),
        };
      })}
      onPick={onPick}
      action={{
        label: t(`${KEY}.reset.action`),
        className: String(styles.resetAction),
        onAction: onReset,
      }}
    />
  );
}

/**
 * „Zwiąż z zakresem" — a real switch, with its refusal on the name rather than in a tooltip.
 *
 * A `warning` says the press DESTROYS something, and it rides on the accessible name for the same
 * reason a refusal does: the reason a control exists has to be reachable from the keyboard.
 */
function BindSwitch({
  isOn,
  refusal,
  warning,
  label,
  onToggle,
}: {
  readonly isOn: boolean;
  readonly refusal: string | null;
  readonly warning: string | null;
  readonly label: string;
  readonly onToggle: () => void;
}) {
  const note = refusal ?? warning;
  return (
    <button
      type="button"
      role="switch"
      className={styles.bind}
      aria-checked={isOn}
      aria-disabled={refusal !== null}
      aria-label={note === null ? undefined : `${label}, ${note}`}
      onClick={() => {
        if (refusal === null) onToggle();
      }}
    >
      <span className={styles.track} aria-hidden="true">
        <span className={styles.knob} />
      </span>
      {label}
    </button>
  );
}
